import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { productLabel } from "@/lib/branding";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { UserPlus, Trash2, Copy, Users, Settings2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

const ASSIGNABLE_ROLES = ["admin", "cfo", "accountant", "manager", "hr", "external_accountant"];

const FEATURE_LABELS = {
  invoicing: "Ventes & Factures",
  expenses: "Achats & Dépenses",
  contracts: "Contrats",
  accounting: "Comptabilité",
  treasury: "Trésorerie",
  hr: "RH & Paie",
  fleet: "Parc Auto",
  legal: "Juridique",
  crm: "CRM",
  ai_copilot: productLabel("IA"),
  tasks: "Tâches",
  pilotage: "Pilotage",
};

export default function Members() {
  const { t } = useTranslation();
  const { session, hasPermission } = useAuth();
  const [members, setMembers] = useState([]);
  const [availableFeatures, setAvailableFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openInvite, setOpenInvite] = useState(false);
  const [inv, setInv] = useState({ email: "", full_name: "", role: "manager", features_override: null });
  const [featuresDialog, setFeaturesDialog] = useState(null); // { user_id, email, features_override }
  const [error, setError] = useState("");
  const [inviteLink, setInviteLink] = useState("");
  const canInvite = hasPermission("users.invite");
  const canManageRoles = hasPermission("users.manage_roles");
  const canRemove = hasPermission("users.remove");

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: mData }, { data: fData }] = await Promise.all([
        api.get("/tenant/members"),
        api.get("/tenant/available-features").catch(() => ({ data: { features: [] } })),
      ]);
      setMembers(mData.members);
      setAvailableFeatures(fData.features || []);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [session?.tenant?.id]);

  const submitInvite = async () => {
    setError("");
    try {
      const { data } = await api.post("/tenant/members/invite", inv);
      const url = `${window.location.origin}/invite/${data.invitation.token}`;
      setInviteLink(url);
      toast.success(t("members.inviteSuccess"));
      await load();
    } catch (e) {
      setError(apiError(e));
    }
  };

  const changeRole = async (userId, role) => {
    try {
      await api.patch(`/tenant/members/${userId}/role`, { role });
      toast.success("Rôle mis à jour");
      await load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const removeMember = async (userId) => {
    if (!confirm(t("members.confirmRemove"))) return;
    try {
      await api.delete(`/tenant/members/${userId}`);
      toast.success("Membre retiré");
      await load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveFeaturesOverride = async () => {
    if (!featuresDialog) return;
    try {
      await api.patch(`/tenant/members/${featuresDialog.user_id}/features`, {
        features_override: featuresDialog.features_override,
      });
      toast.success(featuresDialog.features_override === null
        ? "Accès complet au plan restauré"
        : "Modules mis à jour");
      setFeaturesDialog(null);
      await load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleInviteFeature = (feat) => {
    const current = inv.features_override;
    if (current === null) {
      // Passer en mode restreint : coche seulement celle qu'on active
      setInv({ ...inv, features_override: [feat] });
      return;
    }
    const next = current.includes(feat)
      ? current.filter((f) => f !== feat)
      : [...current, feat];
    setInv({ ...inv, features_override: next });
  };

  const toggleEditFeature = (feat) => {
    const current = featuresDialog.features_override;
    if (current === null) {
      setFeaturesDialog({ ...featuresDialog, features_override: availableFeatures.filter((f) => f !== feat) });
      return;
    }
    const next = current.includes(feat)
      ? current.filter((f) => f !== feat)
      : [...current, feat];
    setFeaturesDialog({ ...featuresDialog, features_override: next });
  };

  return (
    <div className="space-y-6" data-testid="members-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900"
              style={{ fontFamily: "Manrope, sans-serif" }}>
            {t("members.title")}
          </h1>
          <p className="mt-1 text-sm text-slate-500">{t("members.subtitle")}</p>
        </div>
        {canInvite && (
          <Dialog open={openInvite} onOpenChange={(v) => { setOpenInvite(v); if (!v) { setError(""); setInviteLink(""); } }}>
            <DialogTrigger asChild>
              <Button data-testid="invite-open-btn"
                      className="bg-blue-600 hover:bg-blue-700 text-white">
                <UserPlus className="h-4 w-4 mr-1.5" /> {t("members.inviteBtn")}
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="invite-dialog">
              <DialogHeader>
                <DialogTitle>{t("members.inviteTitle")}</DialogTitle>
                <DialogDescription>Le membre recevra un lien d'invitation à activer.</DialogDescription>
              </DialogHeader>
              {error && (
                <Alert variant="destructive" data-testid="invite-error">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              {inviteLink ? (
                <div className="space-y-2">
                  <Label>{t("members.inviteLinkLabel")}</Label>
                  <div className="flex gap-2">
                    <Input readOnly value={inviteLink} data-testid="invite-link-input" />
                    <Button variant="outline"
                            data-testid="copy-invite-link-btn"
                            onClick={() => { navigator.clipboard.writeText(inviteLink); toast.success(t("common.copied")); }}>
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500">
                    Communiquez ce lien au futur membre. Il expire dans 7 jours.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <Label>{t("common.email")}</Label>
                    <Input type="email"
                           data-testid="invite-email-input"
                           value={inv.email}
                           onChange={(e) => setInv({ ...inv, email: e.target.value })}
                           placeholder={t("members.emailPlaceholder")} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>{t("auth.fullName")}</Label>
                    <Input data-testid="invite-name-input"
                           value={inv.full_name}
                           onChange={(e) => setInv({ ...inv, full_name: e.target.value })}
                           placeholder={t("members.namePlaceholder")} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>{t("common.role")}</Label>
                    <Select value={inv.role} onValueChange={(v) => setInv({ ...inv, role: v })}>
                      <SelectTrigger data-testid="invite-role-select">
                        <SelectValue placeholder={t("members.selectRole")} />
                      </SelectTrigger>
                      <SelectContent>
                        {ASSIGNABLE_ROLES.map((r) => (
                          <SelectItem key={r} value={r} data-testid={`invite-role-option-${r}`}>
                            {t(`roles.${r}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {availableFeatures.length > 0 && (
                    <div className="space-y-2 border-t border-slate-100 pt-3">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm">Modules autorisés</Label>
                        <button
                          type="button"
                          data-testid="invite-features-reset-btn"
                          onClick={() => setInv({ ...inv, features_override: inv.features_override === null ? [] : null })}
                          className="text-xs text-blue-600 hover:underline">
                          {inv.features_override === null ? "Restreindre" : "Accès complet"}
                        </button>
                      </div>
                      <p className="text-xs text-slate-500">
                        {inv.features_override === null
                          ? "Accès complet au plan (tous les modules)."
                          : "Cochez uniquement les modules que ce membre pourra voir."}
                      </p>
                      {inv.features_override !== null && (
                        <div className="grid grid-cols-2 gap-2 max-h-52 overflow-y-auto p-2 bg-slate-50 rounded-md border border-slate-200"
                             data-testid="invite-features-grid">
                          {availableFeatures.map((f) => (
                            <label key={f} className="flex items-center gap-2 cursor-pointer text-sm">
                              <Checkbox
                                checked={inv.features_override.includes(f)}
                                onCheckedChange={() => toggleInviteFeature(f)}
                                data-testid={`invite-feature-${f}`} />
                              <span>{FEATURE_LABELS[f] || f}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              <DialogFooter>
                {inviteLink ? (
                  <Button onClick={() => setOpenInvite(false)} data-testid="invite-close-btn"
                          className="bg-blue-600 hover:bg-blue-700 text-white">Terminé</Button>
                ) : (
                  <>
                    <Button variant="outline" onClick={() => setOpenInvite(false)}>{t("common.cancel")}</Button>
                    <Button onClick={submitInvite}
                            data-testid="invite-submit-btn"
                            className="bg-blue-600 hover:bg-blue-700 text-white">
                      {t("common.invite")}
                    </Button>
                  </>
                )}
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card className="border-slate-200 bg-white overflow-hidden rounded-xl">
        {loading ? (
          <div className="p-10 text-center text-slate-500 text-sm">{t("common.loading")}</div>
        ) : members.length === 0 ? (
          <div className="p-16 text-center">
            <Users className="h-8 w-8 text-slate-300 mx-auto" />
            <p className="mt-3 text-sm text-slate-500">{t("members.emptyState")}</p>
          </div>
        ) : (
          <Table data-testid="members-table">
            <TableHeader>
              <TableRow>
                <TableHead>{t("common.member")}</TableHead>
                <TableHead>{t("common.email")}</TableHead>
                <TableHead>{t("common.role")}</TableHead>
                <TableHead className="w-32"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.map((m) => (
                <TableRow key={m.user_id} data-testid={`member-row-${m.email}`}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-xs">
                        {(m.full_name || m.email).charAt(0).toUpperCase()}
                      </div>
                      {m.full_name || m.email}
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-600 text-sm">
                    <div>{m.email}</div>
                    {m.role !== "owner" && availableFeatures.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1" data-testid={`member-modules-${m.email}`}>
                        {m.features_override === null ? (
                          <Badge variant="outline"
                                 className="border-emerald-200 bg-emerald-50 text-emerald-700 text-[10px] px-1.5 py-0 h-5">
                            Accès complet
                          </Badge>
                        ) : m.features_override.length === 0 ? (
                          <Badge variant="outline"
                                 className="border-red-200 bg-red-50 text-red-700 text-[10px] px-1.5 py-0 h-5">
                            Aucun module
                          </Badge>
                        ) : (
                          m.features_override.map((f) => (
                            <Badge key={f} variant="outline"
                                   className="border-slate-200 bg-slate-50 text-slate-700 text-[10px] px-1.5 py-0 h-5">
                              {FEATURE_LABELS[f] || f}
                            </Badge>
                          ))
                        )}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {canManageRoles && m.role !== "owner" ? (
                      <Select value={m.role} onValueChange={(v) => changeRole(m.user_id, v)}>
                        <SelectTrigger data-testid={`role-select-${m.email}`} className="w-48 h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ASSIGNABLE_ROLES.map((r) => (
                            <SelectItem key={r} value={r}>{t(`roles.${r}`)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge variant="outline"
                             className={m.role === "owner"
                               ? "border-blue-200 text-blue-700 bg-blue-50"
                               : "border-slate-200 text-slate-700"}>
                        {t(`roles.${m.role}`)}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {canManageRoles && m.role !== "owner" && availableFeatures.length > 0 && (
                      <Button variant="ghost" size="sm"
                              data-testid={`edit-features-${m.email}`}
                              title={m.features_override
                                ? `Modules restreints : ${m.features_override.length}/${availableFeatures.length}`
                                : "Accès complet plan — cliquer pour restreindre"}
                              onClick={() => setFeaturesDialog({
                                user_id: m.user_id,
                                email: m.email,
                                features_override: m.features_override,
                              })}>
                        <Settings2 className={`h-4 w-4 ${m.features_override ? "text-amber-600" : "text-slate-400"}`} />
                      </Button>
                    )}
                    {canRemove && m.role !== "owner" && m.user_id !== session.user.id && (
                      <Button variant="ghost" size="sm"
                              data-testid={`remove-member-${m.email}`}
                              onClick={() => removeMember(m.user_id)}>
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* Dialog édition modules par membre (Sprint 87 — modules picker) */}
      <Dialog open={!!featuresDialog} onOpenChange={(v) => !v && setFeaturesDialog(null)}>
        <DialogContent data-testid="features-dialog">
          <DialogHeader>
            <DialogTitle>Modules autorisés</DialogTitle>
            <DialogDescription>
              {featuresDialog?.email} — cochez les modules visibles pour ce membre.
            </DialogDescription>
          </DialogHeader>
          {featuresDialog && (
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">
                  {featuresDialog.features_override === null
                    ? "Accès complet au plan (par défaut)"
                    : `${featuresDialog.features_override.length} / ${availableFeatures.length} modules cochés`}
                </span>
                <button
                  type="button"
                  data-testid="features-reset-btn"
                  onClick={() => setFeaturesDialog({ ...featuresDialog, features_override: null })}
                  className="text-xs text-blue-600 hover:underline">
                  Réinitialiser (accès complet)
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto p-2 bg-slate-50 rounded-md border border-slate-200">
                {availableFeatures.map((f) => {
                  const enabled = featuresDialog.features_override === null
                    ? true
                    : featuresDialog.features_override.includes(f);
                  return (
                    <label key={f} className="flex items-center gap-2 cursor-pointer text-sm">
                      <Checkbox
                        checked={enabled}
                        onCheckedChange={() => toggleEditFeature(f)}
                        data-testid={`edit-feature-${f}`} />
                      <span>{FEATURE_LABELS[f] || f}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setFeaturesDialog(null)}>Annuler</Button>
            <Button data-testid="features-save-btn"
                    onClick={saveFeaturesOverride}
                    className="bg-blue-600 hover:bg-blue-700 text-white">
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
